import {useTranslation} from '@deviceinsight/ng-ui-scale-lib/api';
import {SortableElement} from 'react-sortable-hoc';
import {Icon, Button} from '@deviceinsight/ng-ui-basic-components';
import type {Property} from '@deviceinsight/ng-ui-api-client';
import DragHandle from './DragHandle';
import {
	InfoWidgetDatapoint,
	InfoWidgetItem,
	InfoWidgetProperty,
	isInfoWidgetDatapoint,
	isInfoWidgetProperty
} from '../types';
import {InfoWidgetItemCategory} from '../types';
import DatapointConfig from './configItems/DatapointConfig';
import PropertyConfig from './configItems/PropertyConfig';
import React from 'react';

type OwnProps = {
	item: InfoWidgetItem;
	itemIndex: number;
	context: any;
	onSelectDatapoint: (arg0: number) => void;
	onItemChange: (arg0: number, arg1: InfoWidgetItem) => void;
	assetProperties: Array<Property>;
	onRemoveItem: (arg0: number) => void;
};
type Props = OwnProps;
const TYPE_TRANSLATIONS = {
	[InfoWidgetItemCategory.DATAPOINT]: {
		id: 'dashboards.widget.info.type.datapoint',
		defaultValue: 'Datapoint Value'
	},
	[InfoWidgetItemCategory.PROPERTY]: {
		id: 'dashboards.widget.info.type.property',
		defaultValue: 'Property Value'
	}
};

const InfoItem: React.FC<Props> = ({
	item,
	itemIndex,
	assetProperties,
	context,
	onSelectDatapoint,
	onItemChange,
	onRemoveItem
}) => {
	const {t} = useTranslation();

	const handleSelectDatapoint = React.useCallback(() => {
		onSelectDatapoint(itemIndex);
	}, [onSelectDatapoint, itemIndex]);

	const handleLabelChange = React.useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const label = event.target.value;
			onItemChange(itemIndex, {...item, label});
		},
		[onItemChange, itemIndex, item]
	);

	const handlePropertyChange = React.useCallback(
		(event: React.ChangeEvent<HTMLSelectElement>) => {
			const propertyKey = event.target.value;
			if (isInfoWidgetProperty(item)) {
				onItemChange(itemIndex, {...item, propertyKey});
			}
		},
		[onItemChange, itemIndex, item]
	);

	const handleTypeChange = React.useCallback(
		(event: React.ChangeEvent<HTMLSelectElement>) => {
			const type = event.target.value;
			const updatedItem =
				type === InfoWidgetItemCategory.DATAPOINT
					? ({
							label: item.label,
							type,
							datapoint: undefined,
							decimalPoints: 2
					  } as InfoWidgetDatapoint)
					: ({
							label: item.label,
							type,
							propertyKey: ''
					  } as InfoWidgetProperty);
			onItemChange(itemIndex, updatedItem);
		},
		[onItemChange, itemIndex, item.label]
	);

	const handleDecimalPointChange = React.useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const decimalPoints = parseInt(event.target.value, 10);
			if (isInfoWidgetDatapoint(item)) {
				onItemChange(itemIndex, {...item, decimalPoints});
			}
		},
		[onItemChange, itemIndex, item]
	);

	const handleRemoveItem = React.useCallback(() => {
		onRemoveItem(itemIndex);
	}, [onRemoveItem, itemIndex]);

	return (
		<tr key={`itemRow-${itemIndex}`}>
			<td className="customDragHandle">
				<DragHandle />
			</td>
			<td className="info-label">
				<input type="text" value={item.label} onChange={handleLabelChange} />
			</td>
			<td className="data-select">
				<select onChange={handleTypeChange} value={item.type}>
					{Object.keys(TYPE_TRANSLATIONS).map((key, index) => {
						const optionText = t(TYPE_TRANSLATIONS[key].id, {
							defaultValue: TYPE_TRANSLATIONS[key].defaultValue
						});
						return (
							<option key={index} value={key}>
								{optionText}
							</option>
						);
					})}
				</select>
			</td>
			{isInfoWidgetDatapoint(item) ? (
				<DatapointConfig
					item={item}
					context={context}
					t={t}
					onSelectDatapoint={handleSelectDatapoint}
					onDecimalPointChange={handleDecimalPointChange}
				/>
			) : (
				<PropertyConfig
					item={item}
					t={t}
					assetProperties={assetProperties}
					onPropertyChange={handlePropertyChange}
				/>
			)}
			<td className="item-remove">
				<Button onClick={handleRemoveItem}>
					<Icon del />
				</Button>
			</td>
		</tr>
	);
};

const DraggableInfoItem = SortableElement(InfoItem);
export default DraggableInfoItem as unknown as React.ComponentType<OwnProps>;
